import React, { useState, useEffect, useCallback } from 'react';
import { Spin, message } from 'antd';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import dayjs from 'dayjs';
import PageSkeleton, { DetailSkeleton } from '../components/Skeleton';

const GOLD = '#B07820';
const BD = '#D0C8B8';
const BG = '#F7F4EF';
const MUTED = '#64543C';
const MUTED_LIGHT = '#A08C6C';

const GRADE_CONFIG = {
  S: { labelKey: 'inspection.gradeS', color: '#1E5C30', bg: 'rgba(30,92,48,0.08)' },
  A: { labelKey: 'inspection.gradeA', color: '#1A3870', bg: 'rgba(26,56,112,0.08)' },
  B: { labelKey: 'inspection.gradeB', color: '#8C5800', bg: 'rgba(140,88,0,0.08)' },
  C: { labelKey: 'inspection.gradeC', color: MUTED,     bg: 'rgba(100,84,60,0.08)' },
  D: { labelKey: 'inspection.gradeD', color: '#8C1A1A', bg: 'rgba(140,26,26,0.08)' },
};

const GRADE_LEGEND_CONFIG = [
  { grade: 'S', labelKey: 'inspection.gradeSLabel', descKey: 'inspection.gradeSDesc', color: '#1E5C30', bg: 'rgba(30,92,48,0.08)', bd: 'rgba(30,92,48,0.3)' },
  { grade: 'A', labelKey: 'inspection.gradeALabel', descKey: 'inspection.gradeADesc', color: '#1A3870', bg: 'rgba(26,56,112,0.08)', bd: 'rgba(26,56,112,0.3)' },
  { grade: 'B', labelKey: 'inspection.gradeBLabel', descKey: 'inspection.gradeBDesc', color: '#8C5800', bg: 'rgba(140,88,0,0.08)', bd: 'rgba(140,88,0,0.3)' },
  { grade: 'C', labelKey: 'inspection.gradeCLabel', descKey: 'inspection.gradeCDesc', color: MUTED, bg: 'rgba(100,84,60,0.08)', bd: 'rgba(100,84,60,0.3)' },
];

function DimensionBar({ label, value, maxValue = 10, color }) {
  const pct = Math.min((value / maxValue) * 100, 100);
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
        <span style={{ fontFamily: 'var(--mono)', fontSize: 10, color: MUTED_LIGHT, letterSpacing: 1 }}>{label}</span>
        <span style={{ fontFamily: 'var(--mono)', fontSize: 10, color: color || GOLD }}>{value}/{maxValue}</span>
      </div>
      <div style={{ height: 6, background: '#EDE8DC', borderRadius: 3, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: pct + '%', background: color || GOLD, borderRadius: 3, transition: 'width 1s ease' }} />
      </div>
    </div>
  );
}

function Lightbox({ photos, index, onClose, onPrev, onNext }) {
  const { t } = useTranslation();
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') onPrev();
      if (e.key === 'ArrowRight') onNext();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose, onPrev, onNext]);

  if (index < 0 || !photos.length) return null;

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(22,18,8,0.85)',
        zIndex: 300, display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        backdropFilter: 'blur(8px)',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff', borderRadius: 4, padding: 20,
          maxWidth: 640, width: '90%', textAlign: 'center',
          boxShadow: '0 4px 32px rgba(22,18,8,0.14)',
          animation: 'lbIn 0.25s ease',
        }}
      >
        <div style={{
          width: '100%', aspectRatio: '4/3', background: '#F2EEE4',
          borderRadius: 2, overflow: 'hidden', marginBottom: 12,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <img
            src={photos[index]}
            alt={`${t('inspection.photos')} ${index + 1}`}
            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
          />
        </div>
        <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: MUTED_LIGHT, letterSpacing: 1, marginBottom: 12 }}>
          {t('inspection.photos')} ({index + 1}/{photos.length})
        </div>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
          <button
            onClick={onPrev}
            style={{
              padding: '7px 20px', border: '1px solid ' + BD, borderRadius: 2,
              background: 'transparent', fontFamily: 'var(--mono)', fontSize: 10,
              color: MUTED, cursor: 'pointer', transition: 'all 0.14s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = GOLD; e.currentTarget.style.color = GOLD; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = BD; e.currentTarget.style.color = MUTED; }}
          >
            ← {t('inspection.prevPhoto')}
          </button>
          <button
            onClick={onClose}
            style={{
              padding: '7px 20px', border: '1px solid #161208', borderRadius: 2,
              background: '#161208', fontFamily: 'var(--mono)', fontSize: 10,
              color: '#fff', cursor: 'pointer', transition: 'all 0.14s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = '#2C2210'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = '#161208'; }}
          >
            {t('common.close')}
          </button>
          <button
            onClick={onNext}
            style={{
              padding: '7px 20px', border: '1px solid ' + BD, borderRadius: 2,
              background: 'transparent', fontFamily: 'var(--mono)', fontSize: 10,
              color: MUTED, cursor: 'pointer', transition: 'all 0.14s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = GOLD; e.currentTarget.style.color = GOLD; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = BD; e.currentTarget.style.color = MUTED; }}
          >
            {t('inspection.nextPhoto')} →
          </button>
        </div>
      </div>
      <style>{`@keyframes lbIn{from{transform:scale(.9);opacity:0}to{transform:scale(1);opacity:1}}`}</style>
    </div>
  );
}

export default function InspectionReport() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedPhoto, setSelectedPhoto] = useState(0);
  const [lightboxIndex, setLightboxIndex] = useState(-1);

  useEffect(() => {
    const fetchReport = async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/orders/${id}/inspection`);
        setReport(data);
      } catch (err) {
        message.error(t('common.failed'));
      } finally {
        setLoading(false);
      }
    };
    fetchReport();
  }, [id]);

  const r = report || {};
  let photos = [];
  try { photos = typeof r.photo_urls === 'string' ? JSON.parse(r.photo_urls) : (r.photo_urls || []); } catch {}

  const handleLightboxPrev = useCallback(() => {
    setLightboxIndex(prev => (prev - 1 + photos.length) % photos.length);
  }, [photos.length]);

  const handleLightboxNext = useCallback(() => {
    setLightboxIndex(prev => (prev + 1) % photos.length);
  }, [photos.length]);

  const handleLightboxClose = useCallback(() => {
    setLightboxIndex(-1);
  }, []);

  if (loading) {
    return <DetailSkeleton />;
  }

  const gradeCfg = GRADE_CONFIG[r.grade] || GRADE_CONFIG.B;

  const inspectorInfo = [
    { label: t('inspection.inspector'), value: r.inspector_name || '—' },
    { label: t('inspection.inspector'), value: r.inspector_years || '—' },
    { label: t('inspection.details'), value: r.inspector_specialization || '—' },
    { label: t('inspection.inspectionDate'), value: r.inspection_duration || '—' },
    { label: t('inspection.details'), value: r.inspection_location || '—', highlight: true },
    { label: t('inspection.photos'), value: photos.length > 0 ? t('inspection.photoCount', { count: photos.length }) : '—', highlight: true },
  ];

  return (
    <div style={{ background: BG, minHeight: '100vh', padding: '0 0 60px' }}>
      {/* Breadcrumb */}
      <div style={{ background: '#fff', borderBottom: '1px solid ' + BD, padding: '0 24px', height: 56, display: 'flex', alignItems: 'center', gap: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontFamily: 'var(--mono)', fontSize: 10, color: MUTED_LIGHT }}>
          <span style={{ cursor: 'pointer', transition: 'color 0.14s' }} onClick={() => navigate('/mandates')}
            onMouseEnter={(e) => { e.currentTarget.style.color = GOLD; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = MUTED_LIGHT; }}
          >{t('mandates.title')}</span>
          <span style={{ color: '#CCBCA0' }}>›</span>
          <span>{t('inspection.title')}</span>
          <span style={{ color: '#CCBCA0' }}>›</span>
          <span style={{ color: MUTED }}>{r.report_no || id}</span>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 10 }}>
          <button
            onClick={() => window.print()}
            style={{ padding: '7px 16px', borderRadius: 2, border: '1px solid ' + BD, background: 'transparent', fontFamily: 'var(--sans)', fontSize: 12, color: MUTED, cursor: 'pointer', transition: 'all 0.15s' }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = MUTED; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = BD; }}
          >
            {t('inspection.printReport')}
          </button>
        </div>
      </div>

      <div style={{ maxWidth: 960, margin: '0 auto', padding: '32px 24px' }}>

        {/* Actions bar */}
        <div style={{
          background: '#fff', border: '1px solid ' + BD, borderRadius: 4,
          padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 12,
          boxShadow: '0 2px 14px rgba(22,18,8,0.09)', marginBottom: 28,
          flexWrap: 'wrap',
        }}>
          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={{ fontFamily: 'var(--serif)', fontSize: 14, color: '#161208', letterSpacing: 1 }}>
              {t('inspection.completedNotice')}
            </div>
            <div style={{ fontSize: 11, color: MUTED_LIGHT, marginTop: 2 }}>
              {t('orders.orderNo')} {r.order_no || id} · {t('inspection.inspectionDate')} {r.inspected_at ? dayjs(r.inspected_at).format('YYYY-MM-DD HH:mm') + ' JST' : '—'}
            </div>
          </div>
          <button
            onClick={() => navigate('/mandates')}
            style={{
              padding: '9px 20px', borderRadius: 2, border: '1px solid ' + BD,
              background: 'transparent', fontFamily: 'var(--sans)', fontSize: 12,
              color: MUTED, cursor: 'pointer', transition: 'all 0.15s', whiteSpace: 'nowrap',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = MUTED; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = BD; }}
          >
            ← {t('inspection.backToMandate')}
          </button>
          <button
            onClick={() => navigate(`/orders/${id}/payment`)}
            style={{
              padding: '9px 20px', borderRadius: 2, border: '1px solid ' + GOLD,
              background: GOLD, fontFamily: 'var(--sans)', fontSize: 12,
              color: '#fff', cursor: 'pointer', transition: 'all 0.15s', whiteSpace: 'nowrap',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = '#9A6818'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = GOLD; }}
          >
            {t('inspection.confirmAndPay')} →
          </button>
        </div>

        {/* Certificate card */}
        <div style={{ background: '#fff', border: '1px solid ' + BD, borderRadius: 4, overflow: 'hidden', boxShadow: '0 4px 32px rgba(22,18,8,0.1)', marginBottom: 28, position: 'relative' }}>
          {/* Corner ornaments */}
          <div style={{ position: 'absolute', top: 12, left: 12, fontSize: 10, color: 'rgba(176,120,32,0.25)', zIndex: 2 }}>✦</div>
          <div style={{ position: 'absolute', top: 12, right: 12, fontSize: 10, color: 'rgba(176,120,32,0.25)', zIndex: 2 }}>✦</div>

          {/* Cert header - dark */}
          <div style={{ padding: '24px 32px 20px', background: 'linear-gradient(160deg, #161208 0%, #2A1E0C 100%)', position: 'relative', overflow: 'hidden' }}>
            {/* Subtle grid */}
            <div style={{ position: 'absolute', inset: 0, backgroundImage: 'linear-gradient(rgba(176,120,32,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(176,120,32,0.04) 1px, transparent 1px)', backgroundSize: '32px 32px', pointerEvents: 'none' }} />

            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', position: 'relative', zIndex: 1 }}>
              <div>
                <div style={{ fontFamily: 'var(--mono)', fontSize: 8, letterSpacing: 3, color: 'rgba(255,255,255,0.35)', marginBottom: 6 }}>INSPECTION CERTIFICATE</div>
                <div style={{ fontFamily: 'var(--serif)', fontSize: 22, fontWeight: 700, color: '#fff', letterSpacing: 4, lineHeight: 1.2 }}>{t('inspection.title')}</div>
                <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'rgba(255,255,255,0.4)', letterSpacing: 2, marginTop: 4 }}>QUALITY INSPECTION REPORT</div>
              </div>

              {/* Grade badge */}
              {r.grade && (
                <div style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center',
                  width: 80, height: 80, borderRadius: '50%',
                  border: '2.5px solid #C8921E', background: 'rgba(176,120,32,0.12)',
                  justifyContent: 'center',
                }}>
                  <div style={{ fontFamily: 'var(--serif)', fontSize: 32, fontWeight: 700, color: '#C8921E', lineHeight: 1 }}>{r.grade}</div>
                  <div style={{ fontFamily: 'var(--mono)', fontSize: 7, letterSpacing: 2, color: 'rgba(255,255,255,0.4)' }}>GRADE</div>
                </div>
              )}
            </div>

            {/* Meta row */}
            <div style={{ display: 'flex', gap: 24, marginTop: 20, paddingTop: 16, borderTop: '1px solid rgba(255,255,255,0.08)', position: 'relative', zIndex: 1 }}>
              {[
                { label: t('inspection.title'), value: r.report_no || id },
                { label: t('inspection.inspectionDate'), value: r.inspected_at ? dayjs(r.inspected_at).format('YYYY-MM-DD') : '—' },
                { label: t('inspection.inspector'), value: r.inspector_name || t('inspection.professionalInspector') },
              ].map(({ label, value }) => (
                <div key={label}>
                  <div style={{ fontFamily: 'var(--mono)', fontSize: 7, letterSpacing: 2, color: 'rgba(255,255,255,0.3)', marginBottom: 4 }}>{label}</div>
                  <div style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'rgba(255,255,255,0.7)' }}>{value}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Lot info + grade display */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 280px', borderBottom: '1px solid ' + BD }}>
            {/* Lot info */}
            <div style={{ padding: '20px 24px', borderRight: '1px solid ' + BD }}>
              <div style={{ fontFamily: 'var(--mono)', fontSize: 8, letterSpacing: 2, color: MUTED_LIGHT, textTransform: 'uppercase', marginBottom: 10 }}>{t('lotDetail.basicInfo')}</div>
              <div style={{ fontFamily: 'var(--serif)', fontSize: 16, fontWeight: 500, color: '#161208', marginBottom: 8, lineHeight: 1.5 }}>
                {r.lot_title || '—'}
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {r.brand && <span style={{ fontFamily: 'var(--mono)', fontSize: 9, padding: '2px 8px', borderRadius: 2, background: 'rgba(176,120,32,0.08)', border: '1px solid rgba(176,120,32,0.2)', color: GOLD }}>{r.brand}</span>}
                {r.lot_category && <span style={{ fontFamily: 'var(--mono)', fontSize: 9, padding: '2px 8px', borderRadius: 2, background: '#F2EEE4', color: MUTED_LIGHT }}>{r.lot_category.toUpperCase()}</span>}
              </div>
            </div>

            {/* Grade display */}
            <div style={{ padding: '20px 24px' }}>
              <div style={{ fontFamily: 'var(--mono)', fontSize: 8, letterSpacing: 2, color: MUTED_LIGHT, textTransform: 'uppercase', marginBottom: 10 }}>{t('lotDetail.grade')}</div>
              {r.grade ? (
                <div style={{ padding: '16px', background: gradeCfg.bg, border: '1px solid ' + gradeCfg.color + '30', borderRadius: 3 }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 6 }}>
                    <span style={{ fontFamily: 'var(--serif)', fontSize: 40, fontWeight: 700, color: gradeCfg.color, lineHeight: 1 }}>{r.grade}</span>
                    <span style={{ fontSize: 14, color: gradeCfg.color, fontWeight: 500 }}>{t(gradeCfg.labelKey)}</span>
                  </div>
                  {r.grade_notes && <div style={{ fontSize: 12, color: MUTED_LIGHT, lineHeight: 1.6 }}>{r.grade_notes}</div>}
                </div>
              ) : (
                <div style={{ fontSize: 13, color: MUTED_LIGHT }}>{t('common.noData')}</div>
              )}
            </div>
          </div>

          {/* Condition breakdown */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', borderBottom: '1px solid ' + BD }}>
            <div style={{ padding: '20px 24px', borderRight: '1px solid ' + BD }}>
              <div style={{ fontFamily: 'var(--mono)', fontSize: 8, letterSpacing: 2, color: MUTED_LIGHT, textTransform: 'uppercase', marginBottom: 14 }}>{t('inspection.details')}</div>
              <DimensionBar label={`👟 ${t('inspection.leather')}`} value={r.appearance_score ?? 8} />
              <DimensionBar label={`⚙️ ${t('inspection.hardware')}`} value={r.function_score ?? 9} color="#1E5C30" />
              <DimensionBar label={`🧵 ${t('inspection.stitching')}`} value={r.authenticity_score ?? 9} color="#B07820" />
              <DimensionBar label={`🏷️ ${t('inspection.lining')}`} value={r.accessories_score ?? 7} color="#1A3870" />
              <DimensionBar label={`📦 ${t('inspection.accessories')}`} value={r.box_score ?? 6} color="#8C5800" />
            </div>

            <div style={{ padding: '20px 24px' }}>
              <div style={{ fontFamily: 'var(--mono)', fontSize: 8, letterSpacing: 2, color: MUTED_LIGHT, textTransform: 'uppercase', marginBottom: 14 }}>{t('inspection.overall')}</div>
              {[
                { label: t('inspection.appearanceStatus'), value: r.appearance_issue || t('inspection.normal'), type: r.appearance_issue ? 'minor' : 'ok' },
                { label: t('inspection.functionStatus'), value: r.function_issue || t('inspection.normal'), type: r.function_issue ? 'minor' : 'ok' },
                { label: t('inspection.authenticity'), value: r.counterfeit_risk ? t('inspection.concern') : t('inspection.pass'), type: r.counterfeit_risk ? 'warn' : 'ok' },
                { label: t('inspection.accessoriesComplete'), value: r.accessories_status || t('inspection.complete'), type: 'ok' },
                { label: t('inspection.packagingStatus'), value: r.packaging_issue || t('inspection.intact'), type: r.packaging_issue ? 'minor' : 'ok' },
                { label: t('inspection.craftsmanship'), value: t('inspection.excellent'), type: 'ok' },
                { label: t('inspection.overallOpinion'), value: t('inspection.recommendPurchase'), type: 'info' },
              ].map(({ label, value, type }) => {
                const colors = { ok: { bg: 'rgba(30,92,48,.08)', bd: 'rgba(30,92,48,.2)', c: '#1E5C30' }, minor: { bg: 'rgba(140,88,0,.08)', bd: 'rgba(140,88,0,.2)', c: '#8C5800' }, warn: { bg: 'rgba(140,26,26,.08)', bd: 'rgba(140,26,26,.2)', c: '#8C1A1A' }, info: { bg: 'rgba(176,120,32,.08)', bd: 'rgba(176,120,32,.2)', c: '#B07820' } };
                const s = colors[type] || colors.ok;
                return (
                  <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 0', borderBottom: '1px solid #EDE8DC' }}>
                    <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: MUTED_LIGHT }}>{label}</span>
                    <span style={{ fontSize: 10, fontWeight: 500, padding: '2px 10px', borderRadius: 20, background: s.bg, border: `1px solid ${s.bd}`, color: s.c }}>{value}</span>
                  </div>
                );
              })}
              {r.notes && (
                <div style={{ marginTop: 12, padding: '10px 12px', background: '#F2EEE4', border: '1px solid ' + BD, borderRadius: 2, fontSize: 12, color: MUTED, lineHeight: 1.7 }}>
                  {r.notes}
                </div>
              )}
            </div>
          </div>

          {/* Inspector info card */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', borderBottom: '1px solid ' + BD }}>
            <div style={{ gridColumn: '2', padding: '20px 24px' }}>
              <div style={{
                background: '#EEE9E0', border: '1px solid ' + BD, borderRadius: 2, padding: 14,
              }}>
                <div style={{ fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: 2, color: MUTED_LIGHT, textTransform: 'uppercase', marginBottom: 10 }}>{t('inspection.title')}</div>
                {inspectorInfo.map(({ label, value, highlight }) => (
                  <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', fontSize: 12, borderBottom: '1px solid #EDE8DC' }}>
                    <span style={{ color: MUTED_LIGHT }}>{label}</span>
                    <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: highlight ? GOLD : '#2C2210' }}>{value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Signature row */}
          <div style={{ padding: '16px 24px', background: '#F7F4EF', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 10, color: MUTED_LIGHT }}>
              {t('inspection.legalNotice')}
            </div>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 10, color: MUTED_LIGHT }}>
              {t('inspection.authCode')}: {r.auth_code || 'INS-VERIFIED'}
            </div>
          </div>
        </div>

        {/* Photo gallery */}
        {photos.length > 0 && (
          <div style={{ background: '#fff', border: '1px solid ' + BD, borderRadius: 4, overflow: 'hidden', boxShadow: '0 2px 14px rgba(22,18,8,0.08)', marginBottom: 28 }}>
            <div style={{ padding: '14px 20px', borderBottom: '1px solid ' + BD, background: 'linear-gradient(135deg,#faf8f3,#f3ede3)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ fontFamily: 'var(--serif)', fontSize: 14, color: '#161208', letterSpacing: 2 }}>{t('inspection.photos')}</div>
              <div style={{ fontFamily: 'var(--mono)', fontSize: 10, color: MUTED_LIGHT }}>{t('inspection.photoClickHint', { count: photos.length })}</div>
            </div>
            <div style={{ padding: 20 }}>
              {/* Main photo */}
              <div
                onClick={() => setLightboxIndex(selectedPhoto)}
                style={{
                  width: '100%', height: 360, background: '#F2EEE4', borderRadius: 3,
                  overflow: 'hidden', marginBottom: 12, display: 'flex',
                  alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 2px 14px rgba(22,18,8,0.12)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; }}
              >
                {photos[selectedPhoto] ? (
                  <img src={photos[selectedPhoto]} alt={t('inspection.photos')} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                ) : (
                  <span style={{ fontSize: 64 }}>📷</span>
                )}
              </div>
              {/* Thumbnails */}
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {photos.map((url, i) => (
                  <div
                    key={i}
                    onClick={() => { setSelectedPhoto(i); setLightboxIndex(i); }}
                    style={{
                      width: 72, height: 72, borderRadius: 2, overflow: 'hidden', cursor: 'pointer',
                      border: '2px solid ' + (selectedPhoto === i ? GOLD : 'transparent'),
                      background: '#F2EEE4', transition: 'all 0.15s',
                    }}
                    onMouseEnter={(e) => { if (selectedPhoto !== i) e.currentTarget.style.borderColor = MUTED_LIGHT; }}
                    onMouseLeave={(e) => { if (selectedPhoto !== i) e.currentTarget.style.borderColor = 'transparent'; }}
                  >
                    <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Grade legend */}
        <div style={{
          background: '#fff', border: '1px solid ' + BD, borderRadius: 4,
          padding: '18px 20px', boxShadow: '0 2px 14px rgba(22,18,8,0.09)',
        }}>
          <div style={{ fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: 2, color: MUTED_LIGHT, textTransform: 'uppercase', marginBottom: 14 }}>{t('inspection.details')}</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {GRADE_LEGEND_CONFIG.map(({ grade, labelKey, descKey, color, bg, bd }) => (
              <div key={grade} style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                <div style={{
                  width: 32, height: 32, borderRadius: 2, border: '1.5px solid ' + bd,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontFamily: 'var(--serif)', fontSize: 16, fontWeight: 700,
                  flexShrink: 0, background: bg, color: color,
                }}>
                  {grade}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12, fontWeight: 500, color: '#2C2210', marginBottom: 2 }}>{t(labelKey)}</div>
                  <div style={{ fontSize: 11, color: MUTED_LIGHT, lineHeight: 1.6 }}>{t(descKey)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Lightbox overlay */}
      {lightboxIndex >= 0 && photos.length > 0 && (
        <Lightbox
          photos={photos}
          index={lightboxIndex}
          onClose={handleLightboxClose}
          onPrev={handleLightboxPrev}
          onNext={handleLightboxNext}
        />
      )}
    </div>
  );
}
